import React, { useState } from 'react';
import { Plus, Minus, HelpCircle, DollarSign, MousePointer, Clock, Calendar, Brain, Target } from 'lucide-react';

const FAQSection = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  
  // Toggle the expanded state of a FAQ item
  const toggleExpand = (index) => {
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else {
      setExpandedIndex(index);
    }
  };
  
  // FAQ data with icons for visual enhancement
  const faqItems = [
    {
      id: 1,
      icon: <Brain />,
      question: "How does Kinos solve marketing chaos?",
      answer: "Kinos consolidates, automates, and optimizes all your marketing efforts in one AI-powered platform:\n\n• Connect all channels (Social, Email, Ads, Analytics) in one dashboard\n• AI generates content, schedules posts, and optimizes performance\n• Automated A/B testing and real-time optimization\n• Clear reports showing ROI, time saved, and growth metrics",
    },
    {
      id: 2,
      icon: <Target />,
      question: "Who is Kinos perfect for?",
      answer: "Kinos is designed for solo service professionals and small businesses:\n\n• Solo Service Professionals (consultants, coaches, freelancers, therapists)\n• Small & Micro Businesses (teams of 1-10 with limited marketing bandwidth)\n• Anyone spending more time managing marketing than doing their business\n• Professionals who want to look professional and grow consistently",
    },
    {
      id: 3,
      icon: <DollarSign />,
      question: "How much time will I save using Kinos?",
      answer: "Most users save 10-20+ hours every week. Kinos automates your entire marketing workflow:\n\n• Content creation and scheduling across all channels\n• Lead generation and follow-up automation\n• Performance analysis and optimization\n• Cross-channel coordination and reporting",
    },
    {
      id: 4,
      icon: <Clock />,
      question: "Is Kinos easy to use?",
      answer: "Yes! Our intuitive interface is designed specifically for busy professionals, with no tech skills needed:\n\n• Simple 5-step process (Connect, Command, Create, Coordinate, Calibrate)\n• AI handles the complex stuff automatically\n• Pre-built templates and workflows\n• Free onboarding and support included",
    },
    {
      id: 5,
      icon: <HelpCircle />,
      question: "Do I need to be tech-savvy to use Kinos?",
      answer: "Not at all! Kinos was designed for busy professionals, not tech experts. Our platform is as easy to use as social media, with AI handling the complex marketing tasks. If you can use a smartphone, you can use Kinos effectively.",
    },
    {
      id: 6,
      icon: <HelpCircle />,
      question: "Can I integrate Kinos with my existing tools?",
      answer: "Absolutely! Kinos connects seamlessly with the tools you already use:\n\n• Social media platforms (Instagram, LinkedIn, Facebook, Twitter)\n• Email marketing (MailChimp, ConvertKit, ActiveCampaign)\n• Advertising platforms (Google Ads, Meta Ads, LinkedIn Ads)\n• Analytics tools (Google Analytics, Facebook Pixel)\n• Calendar apps (Google Calendar, Outlook, Apple Calendar)",
    }
  ];
  
  return (
    <section id="faq" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center mb-3 bg-[#c4ffa0]/20 px-3 py-1 rounded-full text-xs md:text-sm">
            <span className="mr-1 md:mr-2">❓</span>
            <span className="text-[#2d4a1e] font-medium">FREQUENTLY ASKED QUESTIONS</span>
            <span className="ml-1 md:ml-2">❓</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-3">Got Questions? We've Got Answers</h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about Kinos and how it can transform your marketing chaos into organized success
          </p>
        </div>
        
        {/* FAQ tile grid - responsive layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {faqItems.map((item, index) => (
            <div 
              key={item.id}
              className="bg-white border border-gray-100 rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
              {/* Question header with toggle button */}
              <div 
                className="p-5 md:p-6 flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(index)}
              >
                <div className="flex items-center pr-4">
                  <div className="bg-[#c4ffa0]/10 rounded-full p-2 mr-3 text-[#2d4a1e]">
                    {React.cloneElement(item.icon, { className: 'w-5 h-5 text-[#2d4a1e]' })}
                  </div>
                  <h3 className="font-semibold text-base md:text-lg text-gray-800">{item.question}</h3>
                </div>
                <button 
                  className={`flex-shrink-0 p-1.5 rounded-full transition-colors ${
                    expandedIndex === index 
                      ? 'bg-[#c4ffa0]/20 text-[#2d4a1e]' 
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                  aria-label={expandedIndex === index ? "Collapse" : "Expand"}
                >
                  {expandedIndex === index ? <Minus size={18} /> : <Plus size={18} />}
                </button>
              </div>
              
              {/* Expandable answer section */}
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  expandedIndex === index 
                    ? 'max-h-96' 
                    : 'max-h-0'
                }`}
              >
                <div className="p-5 md:p-6 pt-0 border-t border-gray-100">
                  {item.answer.split('\n\n').map((paragraph, i) => (
                    <React.Fragment key={i}>
                      {paragraph.startsWith('•') ? (
                        <ul className="list-disc pl-5 mb-3 space-y-1">
                          {paragraph.split('\n').map((bulletPoint, j) => (
                            <li key={j} className="text-gray-600">
                              {bulletPoint.replace('• ', '')}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 mb-3">{paragraph}</p>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
      
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default FAQSection;